// Initialize map when Mapbox is ready (loaded via Intersection Observer)
function initDestinationMap() {
  // Check if the destination map block exists on the page
  const mapContainer = document.getElementById("destination-map");
  if (!mapContainer) {
    return;
  }

  // Check if Mapbox GL JS is loaded
  if (typeof mapboxgl === "undefined") {
    console.error("Mapbox GL JS is not loaded");
    return;
  }

  // Get data from WordPress
  const mapBlock = document.querySelector(".destination-map-block");
  if (!mapBlock) {
    return;
  }

  let mapData;
  try {
    mapData = JSON.parse(mapBlock.dataset.destinations);
  } catch (e) {
    console.error("Failed to parse map data:", e);
    return;
  }

  const destinations = mapData.destinations || [];
  const mapboxSettings = mapData.mapbox || {};

  // Check if we have destinations
  if (!destinations.length) {
    console.warn("No destinations available");
    return;
  }

  // Check if access token is available
  const accessToken = mapboxSettings.access_token;
  if (!accessToken) {
    console.error("Mapbox access token not configured");
    mapContainer.innerHTML =
      '<p style="padding: 20px;">Mapbox access token not configured. Please add it in the WordPress settings.</p>';
    return;
  }

  // Set Mapbox access token
  mapboxgl.accessToken = accessToken;

  // Calculate bounds of all destinations
  const bounds = new mapboxgl.LngLatBounds();
  destinations.forEach((dest) => {
    bounds.extend([dest.longitude, dest.latitude]);
  });

  // Initialize the map without a specific center/zoom (will be set by fitBounds)
  const map = new mapboxgl.Map({
    container: "destination-map",
    style: mapboxSettings.style_url || "mapbox://styles/mapbox/light-v11",
    scrollZoom: false, // Disable zoom on scroll
  });

  // Add navigation controls
  map.addControl(new mapboxgl.NavigationControl(), "top-right");

  // Convert destinations to GeoJSON format
  const geojsonData = {
    type: "FeatureCollection",
    features: destinations.map((dest) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [dest.longitude, dest.latitude],
      },
      properties: {
        id: dest.id,
        title: dest.title,
        excerpt: dest.excerpt,
        permalink: dest.permalink,
      },
    })),
  };

  // Track the currently selected destination
  let selectedDestinationId = null;

  // Wait for map to load
  map.on("load", function () {
    // Add the GeoJSON source with clustering enabled
    map.addSource("destinations", {
      type: "geojson",
      data: geojsonData,
      cluster: true,
      clusterMaxZoom: 14, // Max zoom to cluster points
      clusterRadius: 50, // Radius of each cluster when clustering points
    });

    // Add cluster circles layer
    map.addLayer({
      id: "clusters",
      type: "circle",
      source: "destinations",
      filter: ["has", "point_count"],
      paint: {
        // Color depends on point count
        "circle-color": [
          "step",
          ["get", "point_count"],
          "#8e794e", // Color for small clusters
          5,
          "#8e794e", // Color for medium clusters
          10,
          "#8e794e", // Color for large clusters
        ],
        "circle-radius": [
          "step",
          ["get", "point_count"],
          20, // Size for small clusters
          5,
          30, // Size for medium clusters
          10,
          40, // Size for large clusters
        ],
      },
    });

    // Add cluster count labels
    map.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "destinations",
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
      },
      paint: {
        "text-color": "#ffffff",
      },
    });

    // Add individual unclustered points (non-active)
    // Inactive points: brown with brown border
    map.addLayer({
      id: "unclustered-point",
      type: "circle",
      source: "destinations",
      filter: [
        "all",
        ["!", ["has", "point_count"]],
        ["!=", ["get", "id"], selectedDestinationId || ""],
      ],
      paint: {
        "circle-color": "#8e794e",
        "circle-radius": 8,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#8e794e",
      },
    });

    // Add layer for active/selected point
    // Active point: white background with brown border
    map.addLayer({
      id: "active-point",
      type: "circle",
      source: "destinations",
      filter: [
        "all",
        ["!", ["has", "point_count"]],
        ["==", ["get", "id"], selectedDestinationId || ""],
      ],
      paint: {
        "circle-color": "#fff",
        "circle-radius": 10,
        "circle-stroke-width": 3,
        "circle-stroke-color": "#8e794e",
      },
    });

    // When user clicks on a cluster, zoom in
    map.on("click", "clusters", function (e) {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["clusters"],
      });

      if (!features.length) {
        return;
      }

      const clusterId = features[0].properties.cluster_id;
      map
        .getSource("destinations")
        .getClusterExpansionZoom(clusterId, function (err, zoom) {
          if (err) return;

          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom,
          });
        });
    });

    // When user clicks on an individual point, display its information
    map.on("click", "unclustered-point", function (e) {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const properties = e.features[0].properties;

      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      // Update the selected destination ID
      selectedDestinationId = properties.id;

      // Update filters to show only the selected point as active
      map.setFilter("active-point", [
        "all",
        ["!", ["has", "point_count"]],
        ["==", ["get", "id"], properties.id],
      ]);

      map.setFilter("unclustered-point", [
        "all",
        ["!", ["has", "point_count"]],
        ["!=", ["get", "id"], properties.id],
      ]);

      // Update the destination info panel
      updateDestinationInfo(properties);

      // Don't zoom in - just keep the current view
      // All points remain clickable
    });

    // Change cursor to pointer when hovering over clusters
    map.on("mouseenter", "clusters", function () {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "clusters", function () {
      map.getCanvas().style.cursor = "";
    });

    // Change cursor to pointer when hovering over points
    map.on("mouseenter", "unclustered-point", function () {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "unclustered-point", function () {
      map.getCanvas().style.cursor = "";
    });

    // Fit map to show all destinations on initial load
    // This ensures all points are visible from the start
    map.fitBounds(bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 50 },
      maxZoom: 10,
      duration: 0, // No animation on initial load
    });
  });

  /**
   * Update the destination info panel with selected destination data
   */
  function updateDestinationInfo(properties) {
    const titleEl = document.querySelector(".destination-title");
    const excerptEl = document.querySelector(".destination-excerpt");

    if (titleEl) {
      titleEl.textContent = properties.title || "Destination";
    }

    if (excerptEl) {
      excerptEl.textContent = properties.excerpt || "";
    }
  }
}

// Listen for custom mapboxReady event (triggered when Mapbox is lazy-loaded)
document.addEventListener("mapboxReady", initDestinationMap);

// Also initialize on DOMContentLoaded if Mapbox is already loaded (fallback)
document.addEventListener("DOMContentLoaded", function() {
  if (typeof mapboxgl !== "undefined") {
    initDestinationMap();
  }
});
